import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AutosizeModule } from 'ngx-autosize';
import * as Diff from 'diff';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

export interface ChunkToEdit {
  chunkId: string;
  originalValue: string;
  suggestedValue?: string;
}

export interface SuggestionContext {
  nodeId: string;
  oldValue: string;
  nodeLabel: string;
  desiredChange: string;
}

export interface ChunkUpdate {
  chunkId: string;
  oldValue: string;
  newValue: string;
}

@Component({
  selector: 'app-edit-chunks-modal',
  imports: [CommonModule, FormsModule, AutosizeModule],
  templateUrl: './edit-chunks-modal.component.html',
  styleUrls: ['./edit-chunks-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditChunksModalComponent implements OnChanges, OnInit {
  @Input() isOpen = false;
  @Input() chunks: ChunkToEdit[] = [];
  @Input() aiSuggested = false;
  @Input() isUpdatingChunks = false;
  @Input() suggestionContext: SuggestionContext | null = null;
  @Input() isLoadingSuggestions = false;
  @Input() apiError: string | null = null;

  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<ChunkUpdate[]>();
  @Output() clearApiError = new EventEmitter<void>();

  editedChunks: { [key: string]: string } = {};
  diffResults: { [key: string]: Diff.Change[] } = {};
  openAccordionId: string | null = null;
  private textChange$ = new Subject<{ chunkId: string, newValue: string }>();

  // Pre-computed properties replacing getters
  isSuggestionFlow = false;
  displayNodeLabel = '';
  hasChunks = false;

  ngOnInit(): void {
    this.textChange$.pipe(
      debounceTime(300)
    ).subscribe(({ chunkId, newValue }) => {
      const chunk = this.chunks.find(c => c.chunkId === chunkId);
      const original = chunk?.originalValue ?? '';
      this.diffResults[chunkId] = Diff.diffWords(original, newValue);
      // To trigger change detection if needed in OnPush without explicit CDR:
      // Since object reference mutates, we need to create a new object.
      this.diffResults = { ...this.diffResults };
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['suggestionContext']) {
      this.isSuggestionFlow = this.suggestionContext !== null;
      this.displayNodeLabel = (this.suggestionContext?.nodeLabel || '').replace(/__/g, '');
    }

    if (changes['chunks']) {
      this.hasChunks = (this.chunks || []).length > 0;
      if (this.chunks) {
        this.initChunks();
      }
    }
  }

  initChunks(): void {
    const initialEdits: { [key: string]: string } = {};
    const initialDiffs: { [key: string]: Diff.Change[] } = {};

    this.chunks.forEach(chunk => {
      const initialValue = chunk.suggestedValue ?? chunk.originalValue ?? '';
      initialEdits[chunk.chunkId] = initialValue;
      initialDiffs[chunk.chunkId] = Diff.diffWords(
        chunk.originalValue ?? '',
        initialValue
      );
    });

    this.editedChunks = initialEdits;
    this.diffResults = initialDiffs;

    if (this.hasChunks) {
      this.openAccordionId = this.chunks[0].chunkId;
    } else {
      this.openAccordionId = null;
    }
  }

  constructor() { }

  handleTextChange(chunkId: string, newValue: string): void {
    this.editedChunks[chunkId] = newValue;
    this.textChange$.next({ chunkId, newValue });
  }

  handleSaveChanges(): void {
    const changes: ChunkUpdate[] = this.chunks
      .map(chunk => ({
        chunkId: chunk.chunkId,
        oldValue: chunk.originalValue,
        newValue: this.editedChunks[chunk.chunkId]
      }))
      .filter(change => change.newValue !== change.oldValue);

    if (changes.length > 0) {
      this.save.emit(changes);
    } else {
      this.close.emit();
    }
  }

  toggleAccordion(chunkId: string): void {
    this.openAccordionId = this.openAccordionId === chunkId ? null : chunkId;
  }

  trackByChunkId(index: number, chunk: any): string {
    return chunk.chunkId;
  }

  trackByIndex(index: number, item: any): number {
    return index;
  }
}
